// src/lib/kinema/konva/reconciler.ts
import Konva from 'konva';
import type { BaseLayerData } from '../core/types';
import type { KonvaRenderer, RendererEntry } from './renderers/types';
import { resolveLayerAtTime } from '../core/effects/resolveLayer';
import { ROOT_COMP_ID } from '../core/EditorState';
import { GestureOverrideManager } from '../core/tools/GestureOverride';

export class Reconciler {
  private stage: Konva.Stage;
  private mainLayer: Konva.Layer;
  private renderers = new Map<string, KonvaRenderer>();
  private nodeMap = new Map<string, Konva.Node>();
  private prevResolved = new Map<string, BaseLayerData>();
  private prevLayers = new Map<string, BaseLayerData>();
  private prevTime = -1;
  private prevIsPlaying = false;
  private gestureOverrides: GestureOverrideManager;

  constructor(container: HTMLDivElement, width: number, height: number, gestureOverrides?: GestureOverrideManager) {
    this.stage = new Konva.Stage({ container, width, height });
    this.mainLayer = new Konva.Layer();
    this.stage.add(this.mainLayer);
    this.gestureOverrides = gestureOverrides ?? new GestureOverrideManager();
  }

  registerRenderer(type: string, renderer: KonvaRenderer): void {
    this.renderers.set(type, renderer);
  }

  registerRenderers(entries: RendererEntry[]): void {
    for (const [type, renderer] of entries) {
      this.renderers.set(type, renderer);
    }
  }

  update(layers: Record<string, BaseLayerData>, time: number, interpolators?: any, isPlaying: boolean = false): void {
    const resolved = new Map<string, BaseLayerData>();
    const timeChanged = time !== this.prevTime;
    const playingChanged = isPlaying !== this.prevIsPlaying;
    let anyDirty = false;

    // Resolve only dirty layers — skip unchanged ones via reference check
    for (const layer of Object.values(layers)) {
      if (layer.id === ROOT_COMP_ID) continue; // root comp is data-only, not rendered
      if (!layer.visible) continue;
      if (time < layer.startTime || time > layer.endTime) continue;
      const renderer = this.renderers.get(layer.type);
      if (!renderer) continue; // skip non-renderable (like audio)

      const prevLayer = this.prevLayers.get(layer.id);
      const prevResolvedLayer = this.prevResolved.get(layer.id);
      const hasEffects = Object.keys(layer.effects).length > 0;

      // Skip resolve if: same layer reference AND time hasn't changed
      // (or layer has no effects, so time doesn't matter)
      if (prevLayer === layer && prevResolvedLayer && (!timeChanged || !hasEffects)) {
        resolved.set(layer.id, prevResolvedLayer);
        continue;
      }

      // Layer is dirty — resolve it
      resolved.set(layer.id, resolveLayerAtTime(layer, time, interpolators));
      anyDirty = true;
    }

    // Remove nodes for layers no longer present
    for (const [id, node] of this.nodeMap) {
      if (!resolved.has(id)) {
        const renderer = this.renderers.get(this.prevResolved.get(id)?.type ?? '');
        renderer?.destroy(node);
        this.nodeMap.delete(id);
        anyDirty = true;
      }
    }

    // Detect new layers (not in prevResolved)
    if (!anyDirty) {
      for (const id of resolved.keys()) {
        if (!this.prevResolved.has(id)) { anyDirty = true; break; }
      }
    }

    // Skip reconcile + draw if nothing changed AND time hasn't changed
    // (time changes require reconcile for video seeking even if no layers are dirty)
    if (!anyDirty && !timeChanged && !playingChanged) {
      this.prevTime = time;
      this.prevIsPlaying = isPlaying;
      return;
    }

    if (typeof window !== 'undefined' && (window as any).__KINEMA_AUDIO_DEBUG && isPlaying) {
      const now = performance.now();
      const last = (this as any).__lastDrawAt ?? now;
      (this as any).__lastDrawAt = now;
      console.log(`[reconciler] draw playhead=${time.toFixed(3)} dt=${(now - last).toFixed(1)}ms`);
    }
    this.reconcileChildren(ROOT_COMP_ID, resolved, layers, time, isPlaying);
    this.mainLayer.batchDraw();
    this.prevResolved = resolved;
    this.prevLayers = new Map(Object.entries(layers));
    this.prevTime = time;
    this.prevIsPlaying = isPlaying;
  }

  private reconcileChildren(
    parentId: string | null,
    resolved: Map<string, BaseLayerData>,
    allLayers: Record<string, BaseLayerData>,
    time: number = 0,
    isPlaying: boolean = false,
  ): void {
    const children = Array.from(resolved.values())
      .filter((l) => l.parentId === parentId)
      .sort((a, b) => a.order - b.order);

    for (let i = 0; i < children.length; i++) {
      const layer = children[i];
      let node = this.nodeMap.get(layer.id);
      const renderer = this.renderers.get(layer.type);
      if (!renderer) continue;

      if (!node) {
        // Create new node
        node = renderer.create(layer);
        node.setAttr('layerId', layer.id);
        this.nodeMap.set(layer.id, node);
        // Root comp children go directly on mainLayer (root comp isn't rendered)
        const parentNode = (!parentId || parentId === ROOT_COMP_ID)
          ? this.mainLayer
          : this.nodeMap.get(parentId);
        if (parentNode && 'add' in parentNode) {
          (parentNode as Konva.Container).add(node);
        }
        // Initial update with time — ensures videos seek to playhead on first render
        renderer.update(node, layer, {}, time, isPlaying);
      } else {
        // Diff and update
        const prev = this.prevResolved.get(layer.id);
        const changed: Record<string, boolean> = {};
        if (prev) {
          for (const key of Object.keys(layer)) {
            if ((layer as any)[key] !== (prev as any)[key]) {
              changed[key] = true;
            }
          }
        }
        // Always call update with time (videos need it for seeking even if no props changed)
        renderer.update(node, layer, changed, time, isPlaying);
      }

      // Apply spatial transform properties — skip if a gesture (drag/transform) is active
      if (!this.gestureOverrides.isActive(layer.id)) {
        node.offsetX(layer.anchorX);
        node.offsetY(layer.anchorY);
        node.x(layer.x);
        node.y(layer.y);
        node.rotation(layer.rotation);
        node.scaleX(layer.scaleX);
        node.scaleY(layer.scaleY);
        if ('width' in layer) (node as Konva.Shape).width((layer as any).width);
        if ('height' in layer) (node as Konva.Shape).height((layer as any).height);
      }

      // Non-spatial properties always apply regardless of gesture state
      node.opacity(layer.opacity);

      // Apply shadow properties
      node.setAttr('shadowColor', layer.shadowColor);
      node.setAttr('shadowBlur', layer.shadowBlur);
      node.setAttr('shadowOffsetX', layer.shadowOffsetX);
      node.setAttr('shadowOffsetY', layer.shadowOffsetY);
      node.setAttr('shadowOpacity', layer.shadowOpacity);
      node.setAttr('shadowEnabled', layer.shadowBlur > 0 || layer.shadowOpacity > 0);

      // Apply z-order — move to top in sorted order so last = topmost
      node.moveToTop();

      // Recurse into children (for compositions)
      this.reconcileChildren(layer.id, resolved, allLayers, time, isPlaying);
    }
  }

  getStage(): Konva.Stage {
    return this.stage;
  }

  getMainLayer(): Konva.Layer {
    return this.mainLayer;
  }

  getNodeMap(): Map<string, Konva.Node> {
    return this.nodeMap;
  }

  destroy(): void {
    this.stage.destroy();
  }
}
